import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
// The approved Latin font is bundled through index.css -> assets/fonts/fonts.css.
import '@fontsource/parisienne/latin-400.css'
import '@fontsource/fraunces/latin-500-italic.css'
import '@fontsource/fraunces/latin-600.css'
import '@fontsource/fraunces/latin-700.css'
import './index.css'
import App from './App.tsx'
import { recoverStaleChunk } from './lib/chunkRecovery'
import { warmPublicCatalog } from './lib/catalogWarmup'
// Sentry — only initialised when DSN is provided (production/staging).
// In local dev without VITE_SENTRY_DSN set, this is a no-op.
if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
        integrations: [Sentry.browserTracingIntegration()],
        // 10% of transactions sampled — keeps free tier usage very low
        tracesSampleRate: 0.1,
        // No session replay — saves quota on free tier
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        // Drop expected, user-facing conditions — these are handled in the UI,
        // not bugs. Real problems (5xx, network failures) still report normally.
        beforeSend(event, hint) {
            const err = hint?.originalException as { status?: number; name?: string } | undefined
            if (err) {
                if (
                    err.status === 404 ||                    // content not found (deleted / old / mistyped link)
                    err.status === 401 ||                    // not logged in — handled by silent refresh / redirect
                    err.status === 402 ||                    // subscription required — handled by paywall
                    err.name === 'AuthError' ||
                    err.name === 'SubscriptionRequiredError' ||
                    err.name === 'AlreadyEnrolledError'
                ) {
                    return null // don't send to Sentry
                }
            }
            return event
        },
    })
}

// Auto-recover when a stale tab tries to import a code-split chunk that a newer
// deploy has already removed. Vite fires `vite:preloadError` on such failures;
// a one-time reload pulls the fresh index.html (and the new chunk names).
window.addEventListener('vite:preloadError', () => {
    // A chunk can fail to load simply because the user is offline. Reloading then
    // just yields the browser's "no connection" page — the stale-deploy recovery
    // only makes sense online, so skip the reload while offline.
    recoverStaleChunk()
})

warmPublicCatalog()
createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <App />
    </StrictMode>
)
